import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
  Container,
  Stepper,
  Step,
  StepLabel,
  CssBaseline,
  Card,
  CardContent,
  Grid,
  Fade,
  Backdrop,
  Stack,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useMutation, useQuery } from "react-query";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import { StyledDivider as Divider } from "../../../Components/LightDivider";
import client from "../../../API";
import { replaceConsoleError } from '../../../Utils/toastNotifications';
import routes from "../../../Config/routes";
import {
  validateEmail,
  validatePassword,
  capitalizeFirstLetter,
} from "../../../Utils";
import defaultPfp from "../../../Components/globalAssets/ppfLogo.png";

/* -----------------------------------------------------------------------------
 *  Light Theme Configuration - Matching GlobalUserInvite
 * -------------------------------------------------------------------------- */
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#98a7f2',
      dark: '#4f5dd1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#764ba2',
      light: '#9c7bc7',
      dark: '#5a3d7a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      color: '#1e293b',
    },
    h2: {
      fontWeight: 800,
      color: '#1e293b',
    },
    h3: {
      fontWeight: 700,
      color: '#1e293b',
    },
    h4: {
      fontWeight: 700,
      color: '#1e293b',
    },
    h5: {
      fontWeight: 600,
      color: '#1e293b',
    },
    h6: {
      fontWeight: 600,
      color: '#374151',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

/* -----------------------------------------------------------------------------
 *  Styled Components - Matching GlobalUserInvite
 * -------------------------------------------------------------------------- */
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '16px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    transform: 'none',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.3s ease',
    border: '2px solid transparent',
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      border: '2px solid #667eea',
      '& fieldset': {
        borderColor: 'transparent',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: '#374151',
    '&.Mui-focused': {
      color: '#667eea',
    },
  },
}));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  backgroundColor: 'rgba(15, 23, 42, 0.9)',
  backdropFilter: 'blur(8px)',
  zIndex: theme.zIndex.modal + 1,
}));

const WelcomeCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(6),
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
}));

const ReferrerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
}));

const DemoRegister = () => {
  const navigate = useNavigate();
  const { ref } = useParams();
  const referringUserId = ref && !isNaN(Number(ref)) ? Number(ref) : null;
  const referralSource = referringUserId === null ? ref : "";
  const isCreatorRef = referringUserId === null && ref; // It's a creator username if not numeric

  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    phoneNumber: "",
    industry: "",
    bio: "",
    businessType: "",
    isAgency: "",
    monthlySpend: "",
    teamAccounts: "",
    paymentMethod: "",
    referralSource: referralSource,
    referringUserId: referringUserId || ref, // Use ref for both numeric and string refs
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [openDialog, setOpenDialog] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const steps = ["Personal Information", "Company Information", "Business Details"];

  // Fetch creator details if referred by creator
  const { data: creatorData, isLoading: creatorLoading } = useQuery({
    queryKey: ['creatorDetails', ref],
    queryFn: () => client.creators.fetchDetails(ref),
    enabled: !!isCreatorRef,
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      replaceConsoleError('Failed to fetch creator details', error);
    }
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const { mutate: register, isLoading } = useMutation(
    client.companies.demoRegister,
    {
      onSuccess: (data) => {
        showSnackbar("Registration successful! Please check your email for verification.", "success");
        setTimeout(() => {
          navigate(routes.login);
        }, 2000);
      },
      onError: (error) => {
        replaceConsoleError("An error occurred during registration", error);
        let errorMessage = "An error occurred, please try again.";
        
        if (error.code === "ERR_NETWORK") {
          errorMessage = "Network is disconnected!";
        } else if (error.response && error.response.data) {
          if (error.response.data.missing) {
            errorMessage = `Missing required fields: ${error.response.data.missing.join(', ')}`;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }
        
        showSnackbar(errorMessage, "error");
      },
    }
  );

  const handleNext = () => {
    let bValidation = true;
    const requiredFields = getRequiredFieldsForStep(activeStep);

    requiredFields.forEach((item) => {
      if (bValidation && (!userInfo[item] || userInfo[item].length === 0)) {
        showSnackbar(`${capitalizeFirstLetter(item)} field is required!`, "error");
        bValidation = false;
      }
    });

    if (!bValidation) return;

    if (activeStep === 0) {
      if (!validateEmail(userInfo.email)) {
        showSnackbar("Invalid email address!", "error");
        return;
      }

      if (!validatePassword(userInfo.password)) {
        showSnackbar("Password should have 8 or more characters!", "error");
        return;
      }

      if (userInfo.password !== userInfo.confirmPassword) {
        showSnackbar("Passwords should match!", "error");
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      handleRegister();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleRegister = async () => {
    let bValidation = true;
    const requiredFields = ['firstName', 'lastName', 'email', 'password', 'companyName'];

    requiredFields.forEach((item) => {
      if (bValidation && (!userInfo[item] || userInfo[item].trim().length === 0)) {
        showSnackbar(`${capitalizeFirstLetter(item)} field is required!`, "error");
        bValidation = false;
      }
    });

    if (bValidation && !validateEmail(userInfo.email)) {
      showSnackbar("Invalid email address!", "error");
      bValidation = false;
    }

    if (bValidation && !validatePassword(userInfo.password)) {
      showSnackbar("Password should have 8 or more characters!", "error");
      bValidation = false;
    }

    if (bValidation && userInfo.password !== userInfo.confirmPassword) {
      showSnackbar("Passwords should match!", "error");
      bValidation = false;
    }

    if (!bValidation) return;

    const params = {
      firstName: userInfo.firstName.trim(),
      lastName: userInfo.lastName.trim(),
      email: userInfo.email.trim().toLowerCase(),
      password: userInfo.password,
      companyName: userInfo.companyName.trim(),
      
      ...(userInfo.streetAddress && { streetAddress: userInfo.streetAddress.trim() }),
      ...(userInfo.city && { city: userInfo.city.trim() }),
      ...(userInfo.state && { state: userInfo.state.trim() }),
      ...(userInfo.country && { country: userInfo.country }),
      ...(userInfo.phoneNumber && { phoneNumber: userInfo.phoneNumber.trim() }),
      ...(userInfo.industry && { industry: userInfo.industry }),
      ...(userInfo.bio && { bio: userInfo.bio.trim() }),
      ...(userInfo.businessType && { businessType: userInfo.businessType }),
      ...(userInfo.isAgency && { isAgency: userInfo.isAgency }),
      ...(userInfo.monthlySpend && { monthlySpend: userInfo.monthlySpend.trim() }),
      ...(userInfo.teamAccounts && { teamAccounts: userInfo.teamAccounts.trim() }),
      ...(userInfo.paymentMethod && { paymentMethod: userInfo.paymentMethod }),
      ...(userInfo.referralSource && { referralSource: userInfo.referralSource.trim() }),
      ...(userInfo.referringUserId && { referringUserId: userInfo.referringUserId }),
    };

    // Registration data being sent
    register(params);
  };

  const handleCreatorLogin = () => {
    navigate(routes.creatorConnectOnboard);
  };
  
  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const getRequiredFieldsForStep = (step) => {
    switch (step) {
      case 0:
        return ["firstName", "lastName", "email", "password", "confirmPassword"];
      case 1:
        return ["companyName", "streetAddress", "city", "state", "country", "industry"];
      case 2:
        return ["businessType", "isAgency", "monthlySpend", "teamAccounts", "paymentMethod"];
      default:
        return [];
    }
  };

  const industryOptions = [
    "Fashion & Beauty",
    "Technology",
    "Food & Beverage",
    "Health & Fitness",
    "Sports & Recreation",
    "Travel & Tourism",
    "Finance & Banking",
    "Education",
    "Entertainment & Media",
    "Automotive",
    "Real Estate",
    "Hospitality",
    "Retail & E-commerce",
    "Manufacturing",
    "Marketing Agency",
    "Telecommunications",
    "Gaming",
    "Home & Garden",
    "Pets & Animals",
    "Other",
  ];

  const countryOptions = [
    "United States",
    "Canada",
    "United Kingdom",
    "Australia",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Japan",
    "China",
    "India",
    "Brazil",
    "Mexico",
    "Russia",
    "South Korea",
    "South Africa",
    "Netherlands",
    "Sweden",
    "Singapore",
    "Other",
  ];

  const renderPersonalInfoStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
        Personal Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="First Name"
            name="firstName"
            value={userInfo.firstName}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={!userInfo.firstName && snackbarSeverity === "error"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={userInfo.lastName}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={!userInfo.lastName && snackbarSeverity === "error"}
          />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={userInfo.email}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={!validateEmail(userInfo.email) && userInfo.email.length > 0}
            helperText={!validateEmail(userInfo.email) && userInfo.email.length > 0 ? "Please enter a valid email address" : ""}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={userInfo.password}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={!validatePassword(userInfo.password) && userInfo.password.length > 0}
            helperText={!validatePassword(userInfo.password) && userInfo.password.length > 0 ? "Password must be at least 8 characters" : ""}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={userInfo.confirmPassword}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={userInfo.password !== userInfo.confirmPassword && userInfo.confirmPassword.length > 0}
            helperText={userInfo.password !== userInfo.confirmPassword && userInfo.confirmPassword.length > 0 ? "Passwords do not match" : ""}
          />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Phone Number (Optional)"
            name="phoneNumber"
            value={userInfo.phoneNumber}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="+1 (555) 123-4567"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderCompanyInfoStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
        Company Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Company Name"
            name="companyName"
            value={userInfo.companyName}
            onChange={handleChange}
            required
            disabled={isLoading}
            error={!userInfo.companyName && snackbarSeverity === "error"}
          />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Street Address"
            name="streetAddress"
            value={userInfo.streetAddress}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="City"
            name="city"
            value={userInfo.city}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="State/Province"
            name="state"
            value={userInfo.state}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontWeight: 500, color: '#374151' }}>Country</InputLabel>
            <Select
              name="country"
              value={userInfo.country}
              onChange={handleChange}
              label="Country"
              disabled={isLoading}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#ffffff' },
                '&.Mui-focused': { backgroundColor: '#ffffff' },
              }}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontWeight: 500, color: '#374151' }}>Industry</InputLabel>
            <Select
              name="industry"
              value={userInfo.industry}
              onChange={handleChange}
              label="Industry"
              disabled={isLoading}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#ffffff' },
                '&.Mui-focused': { backgroundColor: '#ffffff' },
              }}
            >
              {industryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Company Description"
            name="bio"
            multiline
            rows={4}
            value={userInfo.bio}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="Tell us about your company and what you do..."
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBusinessDetailsStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
        Business Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontWeight: 500, color: '#374151' }}>Business Structure</InputLabel>
            <Select
              name="businessType"
              value={userInfo.businessType}
              onChange={handleChange}
              label="Business Structure"
              disabled={isLoading}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#ffffff' },
                '&.Mui-focused': { backgroundColor: '#ffffff' },
              }}
            >
              <MenuItem value="One Company">Single Company</MenuItem>
              <MenuItem value="Separate">Multiple Companies</MenuItem>
              <MenuItem value="Holding Company">Holding Company</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontWeight: 500, color: '#374151' }}>Are you an agency?</InputLabel>
            <Select
              name="isAgency"
              value={userInfo.isAgency}
              onChange={handleChange}
              label="Are you an agency?"
              disabled={isLoading}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#ffffff' },
                '&.Mui-focused': { backgroundColor: '#ffffff' },
              }}
            >
              <MenuItem value="Yes">Yes, we're an agency</MenuItem>
              <MenuItem value="No">No, we're a brand</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Expected Monthly Spend ($)"
            name="monthlySpend"
            value={userInfo.monthlySpend}
            onChange={handleChange}
            required
            disabled={isLoading}
            placeholder="e.g., $5,000"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Team Size"
            name="teamAccounts"
            value={userInfo.teamAccounts}
            onChange={handleChange}
            required
            disabled={isLoading}
            placeholder="How many team members will use this account?"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontWeight: 500, color: '#374151' }}>Preferred Payment Method</InputLabel>
            <Select
              name="paymentMethod"
              value={userInfo.paymentMethod}
              onChange={handleChange}
              label="Preferred Payment Method"
              disabled={isLoading}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#ffffff' },
                '&.Mui-focused': { backgroundColor: '#ffffff' },
              }}
            >
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer/ACH</MenuItem>
              <MenuItem value="PayPal">PayPal</MenuItem>
              <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="How did you hear about us?"
            name="referralSource"
            value={userInfo.referralSource}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="Google, friend referral, social media, etc."
            required={!userInfo.referringUserId}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      
      {/* Loading Backdrop */}
      <StyledBackdrop open={isLoading}>
        <CircularProgress color="inherit" size={60} />
      </StyledBackdrop>

      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      }}>
        <HomeHeader />
        <Toolbar />

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          {/* Creator Referrer Card - Similar to GlobalUserInvite */}
          {isCreatorRef && creatorData && !creatorLoading && (
            <ReferrerCard>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 2 }}>
                  <Avatar 
                    src={creatorData?.creator?.pfphref || defaultPfp} 
                    sx={{ width: 80, height: 80, border: '3px solid white' }}
                  >
                    {creatorData?.creator?.creator ? creatorData.creator.creator.charAt(0).toUpperCase() : 'C'}
                  </Avatar>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                      You're Invited!
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      @{creatorData?.creator?.creator} invited you to join Blitz
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {creatorData?.creator?.display_name || creatorData?.creator?.creator}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Join thousands of brands building successful partnerships with creators
                </Typography>
              </CardContent>
            </ReferrerCard>
          )}

          {/* Welcome Header - Show when no creator referrer or while loading creator */}
          {(!isCreatorRef || creatorLoading) && (
            <WelcomeCard>
              <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 2 }}>
                  Welcome to Blitz!
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  Enter your information to continue with Blitz
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Join thousands of brands building successful partnerships with creators
                </Typography>
              </CardContent>
            </WelcomeCard>
          )}

          {/* Registration Form */}
          <Fade in={true}>
            <Box>
              {/* Progress Stepper */}
              <Card sx={{ borderRadius: '20px', border: '1px solid #f1f5f9', mb: 4 }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
                    {steps.map((label, index) => (
                      <Step key={label}>
                        <StepLabel>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            color: activeStep >= index ? '#1e293b' : '#94a3b8',
                            fontSize: { xs: '0.875rem', md: '1rem' }
                          }}>
                            {label}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>

              {/* Form Content */}
              <Card sx={{ borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                <CardContent sx={{ p: { xs: 3, md: 6 } }}>
                  <Stack spacing={4}>
                    {activeStep === 0 && renderPersonalInfoStep()}
                    {activeStep === 1 && renderCompanyInfoStep()}
                    {activeStep === 2 && renderBusinessDetailsStep()}

                    {/* Referral Info Display */}
                    {userInfo.referringUserId && (
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: '#f0f9ff', 
                        borderRadius: '12px', 
                        border: '1px solid #bae6fd',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>
                          {isCreatorRef ? `Referred by Creator: @${ref}` : `Referred by User ID: ${userInfo.referringUserId}`}
                        </Typography>
                      </Box>
                    )}

                    {/* Navigation Buttons */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 4,
                        borderTop: '1px solid #f1f5f9',
                      }}
                    >
                      <Button
                        onClick={handleBack}
                        disabled={activeStep === 0 || isLoading}
                        sx={{
                          borderRadius: '12px',
                          px: 4,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Back
                      </Button>
                      <GradientButton 
                        onClick={handleNext} 
                        disabled={isLoading}
                        sx={{ px: 6 }}
                      >
                        {isLoading && activeStep === steps.length - 1 ? (
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        {activeStep === steps.length - 1 ? "Create Account" : "Next"}
                      </GradientButton>
                    </Box>

                    {/* Help Text */}
                    <Typography
                      variant="body2"
                      sx={{ 
                        textAlign: 'center', 
                        color: 'text.secondary', 
                        pt: 2, 
                        borderTop: '1px solid #f1f5f9' 
                      }}
                    >
                      By creating an account, you'll be able to connect with creators and run successful campaigns.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Fade>

          {/* Creator Section */}
         
        </Container>

        <HomeFooter />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            '&.MuiAlert-standardError': {
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              color: '#166534',
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: '#fffbeb',
              border: '1px solid #fed7aa',
              color: '#92400e',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Welcome Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '1px solid #f1f5f9',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: '#1e293b' }}>
          Thanks for Booking a Creator!
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: 4 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Thanks for booking a creator through their media kit. To access your
            collaboration, make an account here.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            For any questions or concerns, please contact{" "}
            <a 
              href="mailto:valentina@useblitz.co"
              style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}
            >
              valentina@useblitz.co
            </a>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <GradientButton onClick={handleCloseDialog}>
            Get Started
          </GradientButton>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DemoRegister;